import i18n from '../i18n';

/**
 * Get the current locale from i18n
 */
export const getCurrentLocale = (): string => {
  return i18n.language || 'en';
};

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Format a date according to the current locale
 */
export const formatDate = (
  date: Date | string,
  options?: Intl.DateTimeFormatOptions
): string => {
  const locale = getCurrentLocale();

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };

  // Date-only strings (YYYY-MM-DD) are parsed by `new Date()` as midnight UTC,
  // which renders as the previous calendar day in negative UTC-offset
  // timezones. Interpret and format such values in UTC so the calendar day is
  // preserved regardless of the viewer's timezone.
  if (typeof date === 'string' && DATE_ONLY_REGEX.test(date)) {
    const utcDate = new Date(`${date}T00:00:00Z`);
    return new Intl.DateTimeFormat(locale, {
      ...(options || defaultOptions),
      timeZone: 'UTC',
    }).format(utcDate);
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  return new Intl.DateTimeFormat(locale, options || defaultOptions).format(dateObj);
};

/**
 * Format a date as a short date (e.g., "Jan 1, 2024")
 */
export const formatShortDate = (date: Date | string): string => {
  return formatDate(date, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Format a date as a long date (e.g., "January 1, 2024")
 */
export const formatLongDate = (date: Date | string): string => {
  return formatDate(date, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Format a date with time (e.g., "Jan 1, 2024, 10:30 AM")
 */
export const formatDateTime = (date: Date | string): string => {
  return formatDate(date, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * Format a date as month and year only (e.g., "January 2024")
 */
export const formatMonthYear = (date: Date | string): string => {
  return formatDate(date, {
    year: 'numeric',
    month: 'long',
  });
};

/**
 * Format a number according to the current locale
 */
export const formatNumber = (
  value: number,
  options?: Intl.NumberFormatOptions
): string => {
  const locale = getCurrentLocale();
  return new Intl.NumberFormat(locale, options).format(value);
};

/**
 * Format a number as currency
 */
export const formatCurrency = (
  value: number,
  currency: string = 'USD'
): string => {
  const locale = getCurrentLocale();
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Format a number with specific decimal places
 */
export const formatDecimal = (
  value: number,
  minimumFractionDigits: number = 2,
  maximumFractionDigits: number = 2
): string => {
  return formatNumber(value, {
    minimumFractionDigits,
    maximumFractionDigits,
  });
};

/**
 * Format a percentage
 */
export const formatPercent = (value: number): string => {
  const locale = getCurrentLocale();
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Get month options for the last N months
 */
export const getMonthOptions = (count: number = 12): { value: string; label: string }[] => {
  const locale = getCurrentLocale();
  const options: { value: string; label: string }[] = [];
  const today = new Date();
  
  for (let i = 0; i < count; i++) {
    const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const value = date.toISOString().slice(0, 7);
    const label = new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'long' }).format(date);
    options.push({ value, label });
  }
  
  return options;
};

/**
 * Map organization role enum value to translation key
 */
export const getOrganizationRoleKey = (role: string): string => {
  const roleMap: Record<string, string> = {
    'Engineer': 'engineer',
    'Sr. Engineer': 'srEngineer',
    'Tech Lead': 'techLead',
    'Architect': 'architect',
    'Engineering Manager': 'engineeringManager',
    'Director of Engineering': 'directorOfEngineering',
    'Executive': 'executive',
    'HR': 'hr',
    'HR Leader': 'hrLeader',
    'QA': 'qa',
  };
  return roleMap[role] || role.toLowerCase().replace(/\s+/g, '');
};

/**
 * Map invoice status enum value to translation key
 */
export const getStatusKey = (status: string): string => {
  const statusMap: Record<string, string> = {
    'pending': 'pending',
    'under_review': 'underReview',
    'approved': 'approved',
    'rejected': 'rejected',
    'paid': 'paid',
  };
  return statusMap[status] || status;
};

/**
 * Map audit action enum value to translation key
 */
export const getAuditActionKey = (action: string): string => {
  const actionMap: Record<string, string> = {
    'created': 'created',
    'status_changed': 'statusChanged',
    'notes_updated': 'notesUpdated',
    'reviewer_comments_updated': 'reviewerCommentsUpdated',
    'deleted': 'deleted',
  };
  return actionMap[action] || action;
};

/**
 * Normalize a date input value by replacing invalid years (< 1900 or non-4-digit).
 * Handles both zero-padded ("0001") and non-padded ("1") year values that
 * browsers may produce.
 *
 * @param value - A date string in YYYY-MM-DD format from an HTML date input
 * @param defaultYear - Year to substitute when the input year is invalid.
 *                      Defaults to `currentYear - 1` (suitable for birth dates).
 * @returns The normalized YYYY-MM-DD string, or the original value if already valid
 */
export const normalizeDateValue = (
  value: string,
  defaultYear: number = new Date().getFullYear() - 1,
): string => {
  if (!value) return value;
  const parts = value.split('-');
  if (parts.length >= 3) {
    const yearNum = parseInt(parts[0], 10);
    if (parts[0].length < 4 || isNaN(yearNum) || yearNum < 1900) {
      parts[0] = String(defaultYear);
      return parts.join('-');
    }
  }
  return value;
};

/**
 * Convert a date-only string (YYYY-MM-DD) to an ISO string with noon UTC time.
 * This prevents timezone issues where midnight UTC shifts to the previous day
 * in negative UTC offset timezones (e.g., Americas).
 * 
 * @param dateString - A date string in YYYY-MM-DD format from an HTML date input
 * @returns Date-only string in YYYY-MM-DD format (e.g., "1990-05-15")
 */
export const dateToISOString = (dateString: string): string => {
  if (!dateString) return '';
  const normalized = normalizeDateValue(dateString.split('T')[0]);
  return normalized;
};

/**
 * Convert a Date object or ISO string to a date-only string (YYYY-MM-DD) for HTML date inputs.
 * Uses UTC methods to extract the date portion, avoiding local timezone conversion issues.
 * 
 * @param date - A Date object or ISO date string
 * @returns Date string in YYYY-MM-DD format
 */
export const dateToInputValue = (date: Date | string | null | undefined): string => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  
  // Use UTC methods to avoid timezone conversion
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Create a Date object from a date-only string (YYYY-MM-DD) at noon UTC.
 * This prevents timezone issues when the Date object is later converted back.
 * 
 * @param dateString - A date string in YYYY-MM-DD format
 * @returns Date object set to noon UTC on the specified date
 */
export const inputValueToDate = (dateString: string): Date | null => {
  if (!dateString) return null;
  // Parse as noon UTC to avoid timezone shifting
  const date = new Date(`${dateString}T12:00:00.000Z`);
  return isNaN(date.getTime()) ? null : date;
};

/** Today's date in the user's local timezone as YYYY-MM-DD (no UTC conversion). */
export const todayLocalISO = (): string => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

/**
 * Format a date-only string (YYYY-MM-DD) for display in the current locale
 * without timezone conversion, avoiding off-by-one-day shifts.
 */
export const formatDateOnly = (dateString: string): string => {
  if (!dateString) return '';
  const d = new Date(`${dateString.split('T')[0]}T00:00:00Z`);
  if (isNaN(d.getTime())) return dateString;
  return new Intl.DateTimeFormat(getCurrentLocale(), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(d);
};
